import { router } from "@inertiajs/react"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { StatusBadge } from "@/components/ui/status-badge"
import { CancelReasonDialog } from "@/features/staff/cancel-reason-dialog"
import { formatMoney } from "@/lib/formatters"
import { t } from "@/lib/i18n"

/**
 * What is out, who has it, and what went wrong.
 *
 * **The board only ever offers the next legal move.** `Location::Delivery`
 * raises on an out-of-order transition, and a screen that offered "entregada"
 * on an unassigned delivery would be inviting a flash instead of doing work. So
 * each row renders exactly one advance control, chosen from its own status.
 *
 * Failure is reachable from anywhere before delivery, because the ways a
 * delivery fails are not orderly: nobody home, wrong address, courier crashed.
 * The reason is CHOSEN from `Location::DeliveryFailurePolicy`, never typed —
 * "nadie abrió" and "no contestó" are one fact spelled two ways, and a manager
 * asking how often nobody answers the door would get two rows and no answer.
 * That is also why this reuses `CancelReasonDialog` rather than growing a
 * fourth reason dialog: the dialog's whole contract is that the value is
 * chosen.
 */

export type Delivery = {
  id: number
  status: string
  statusLabel: string
  address: string
  deliveryFee: string
  billId: number
  zoneName: string | null
  courierId: number | null
  courierName: string | null
  assignedAt: string | null
  pickedUpAt: string | null
}

export type CourierOption = { value: string; label: string; testId: string }
export type FailureReason = { value: string; label: string; testId: string }

function tBoard(key: string, options: Record<string, string | number> = {}) {
  return t(`ui.react.pages.staff.location.deliveries.index.${key}`, options)
}

const TONE: Record<string, "reserved" | "occupied" | "available" | "danger"> = {
  pending: "reserved",
  assigned: "occupied",
  picked_up: "occupied",
  delivered: "available",
  failed: "danger",
}

function AdvanceControl({ delivery, couriers }: { delivery: Delivery; couriers: CourierOption[] }) {
  if (delivery.status === "pending") {
    // No courier on shift means no assignment is possible, and saying so beats
    // a dropdown with nothing in it.
    if (couriers.length === 0) {
      return (
        <span className="text-muted-foreground text-xs" data-testid={`delivery-no-courier-${delivery.id}`}>
          {tBoard("no_courier")}
        </span>
      )
    }

    return (
      // Native rather than the kit `Select`: the per-option `data-testid` the
      // Playwright suite asserts on only survives on real `<option>` elements,
      // and a native picker is the better control on the phone this is used
      // from. The classes are the kit `Input`'s, so it keeps the focus ring, the
      // 16px mobile size that stops iOS zooming, and the dark-mode fill.
      // `aria-label` because a disabled first option is a placeholder, not an
      // accessible name.
      <select
        className="border-input dark:bg-input/30 h-11 rounded-md border bg-transparent px-3 text-base shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] md:h-9 md:text-sm"
        defaultValue=""
        aria-label={tBoard("assign_placeholder")}
        data-testid={`delivery-assign-${delivery.id}`}
        onChange={(event) =>
          router.patch(
            `/deliveries/${delivery.id}/assign`,
            { courier_id: event.target.value },
            { preserveScroll: true }
          )
        }
      >
        <option value="" disabled>
          {tBoard("assign_placeholder")}
        </option>
        {couriers.map((courier) => (
          <option key={courier.value} value={courier.value} data-testid={courier.testId}>
            {courier.label}
          </option>
        ))}
      </select>
    )
  }

  const next = delivery.status === "assigned" ? "pick_up" : delivery.status === "picked_up" ? "deliver" : null
  if (next === null) return null

  return (
    <Button
      className="h-11 md:h-9"
      data-testid={`delivery-${next}-${delivery.id}`}
      onClick={() => router.patch(`/deliveries/${delivery.id}/${next}`, {}, { preserveScroll: true })}
    >
      {tBoard(next)}
    </Button>
  )
}

function DeliveryRow({
  delivery,
  couriers,
  failureReasons,
}: {
  delivery: Delivery
  couriers: CourierOption[]
  failureReasons: FailureReason[]
}) {
  return (
    <li className="space-y-2 px-4 py-3" data-testid={`delivery-${delivery.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium">{delivery.address}</span>
          <span className="text-muted-foreground block truncate text-xs tabular-nums">
            {delivery.courierName
              ? tBoard("row_meta_courier", {
                  courier: delivery.courierName,
                  fee: formatMoney(delivery.deliveryFee),
                })
              : tBoard("row_meta", { fee: formatMoney(delivery.deliveryFee) })}
            {delivery.zoneName ? ` · ${delivery.zoneName}` : ""}
          </span>
        </span>

        <StatusBadge
          tone={TONE[delivery.status] ?? "disabled"}
          label={delivery.statusLabel}
          data-testid={`delivery-status-${delivery.id}`}
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <AdvanceControl delivery={delivery} couriers={couriers} />

        <CancelReasonDialog
          path={`/deliveries/${delivery.id}/fail`}
          method="patch"
          payload={(reason) => ({ failure_reason: reason })}
          reasons={failureReasons}
          testIdPrefix={`delivery-fail-${delivery.id}`}
          triggerTestId={`delivery-fail-trigger-${delivery.id}`}
          triggerLabel={tBoard("fail")}
          copy={{
            title: tBoard("fail_title"),
            description: tBoard("fail_description"),
            reasonLabel: tBoard("fail_reason_label"),
            reasonPlaceholder: tBoard("fail_reason_placeholder"),
            cancel: tBoard("fail_cancel"),
            confirm: tBoard("fail_confirm"),
            submitting: tBoard("fail_submitting"),
          }}
          confirmVariant="destructive"
        />
      </div>
    </li>
  )
}

export function DeliveryBoard({
  deliveries,
  couriers,
  failureReasons,
}: {
  deliveries: Delivery[]
  couriers: CourierOption[]
  failureReasons: FailureReason[]
}) {
  return (
    <Card density="compact" data-testid="delivery-board">
      <CardHeader className="border-b">
        <CardTitle>{tBoard("title")}</CardTitle>
      </CardHeader>

      <CardContent>
        {deliveries.length === 0 ? (
          <p className="text-muted-foreground text-sm" data-testid="delivery-empty">
            {tBoard("empty")}
          </p>
        ) : (
          <ul className="divide-border divide-y rounded-md border" data-testid="delivery-list">
            {deliveries.map((delivery) => (
              <DeliveryRow
                key={delivery.id}
                delivery={delivery}
                couriers={couriers}
                failureReasons={failureReasons}
              />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}
